import { useState, useEffect } from 'react';
import EntityLink from '../components/EntityLink';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import ConsultingInvoiceModal from '../components/ConsultingInvoiceModal';
import PurchaseOrderCreateDrawer from '../components/PurchaseOrderCreateDrawer';

const ENGAGEMENT_LABELS = {
  contract_placement: 'Contract Placement',
  recurring_referral: 'Recurring Referral',
  fte_placement: 'FTE Placement',
  business_consulting: 'Business Consulting',
};
const FREQUENCY_LABELS = { weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', bimonthly: 'Bimonthly', ad_hoc: 'Ad hoc' };

function fmt(n) {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function WorkOrderProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('Details');
  const [showInvoice, setShowInvoice] = useState(false);
  const [showPo, setShowPo] = useState(false);

  function load() {
    setError(false);
    api.get(`/work-orders/${id}`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }
  useEffect(() => { load(); }, [id]);

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;
  const isConsulting = data.engagement_type === 'business_consulting';
  const TABS = isConsulting ? ['Details', 'Invoices', 'Purchase Orders', 'Documents'] : ['Details', 'Purchase Orders', 'Documents'];

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
          
          <h1 className="page-title">{data.client_wo_number}</h1>
          <Badge variant="info">{ENGAGEMENT_LABELS[data.engagement_type] || data.engagement_type}</Badge>
          <Badge variant={data.status === 'active' ? 'success' : 'neutral'}>{data.status}</Badge>
        </div>
        {isConsulting && (
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={() => setShowPo(true)}>+ Client PO (optional)</button>
            <button className="btn btn-primary" onClick={() => setShowInvoice(true)}>+ New consulting invoice</button>
          </div>
        )}
      </div>

      <div className="panel-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Details' && <DetailsTab data={data} />}

      {tab === 'Invoices' && <ConsultingInvoicesTab invoices={data.ar_invoices} />}

      {tab === 'Purchase Orders' && <PurchaseOrdersTab purchaseOrders={data.purchase_orders} isConsulting={isConsulting} />}

      {showInvoice && (
        <ConsultingInvoiceModal workOrder={data} onClose={() => setShowInvoice(false)}
          onSaved={() => { setShowInvoice(false); setTab('Invoices'); load(); }} />
      )}
      {showPo && (
        <PurchaseOrderCreateDrawer workOrder={data} onClose={() => setShowPo(false)} onSaved={load} />
      )}

      {tab === 'Documents' && (
        <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
          <DocumentWallet entityType="work_order" entityId={data.id} entityName={data.client_wo_number} />
        
      <AuditLog tableName="work_orders" recordId={id} /></div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function DetailsTab({ data }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Work Order Details</h3>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <Field label="Client WO #" value={data.client_wo_number} />
        <Field label="Outgoing WO #" value={data.outgoing_wo_number} />
        <Field label="Engagement Type" value={ENGAGEMENT_LABELS[data.engagement_type] || data.engagement_type} />
        <Field label="Client" value={data.client_name} />
        {data.engagement_type === 'business_consulting' ? (
          <>
            <Field label="Billing Frequency" value={FREQUENCY_LABELS[data.billing_frequency] || data.billing_frequency} />
            <Field label="Scope" value={data.scope_description} />
          </>
        ) : (
          <>
            <Field label="Vendor" value={data.vendor_name} />
            <Field label="Contractor" value={data.contractor_name} />
          </>
        )}
        <Field label="Start Date" value={data.start_date} />
        <Field label="End Date" value={data.end_date} />
        <Field label="Status" value={data.status} />
        {!['fte_placement', 'business_consulting'].includes(data.engagement_type) && (
          <>
            <Field label="Sell Rate" value={data.sell_rate ? `$${data.sell_rate}/hr` : null} />
            <Field label="Buy Rate" value={data.buy_rate ? `$${data.buy_rate}/hr` : null} />
          </>
        )}
        {data.engagement_type === 'fte_placement' && (
          <Field label="FTE Fee" value={data.fte_fee_amount ? fmt(data.fte_fee_amount) + (data.fte_fee_invoiced ? ' (Invoiced)' : ' (Not yet invoiced)') : null} />
        )}
        {data.person_link && (
          <Field label="Commission Beneficiary" value={`${data.person_link.first_name} ${data.person_link.last_name}`} />
        )}
      </div>
      {data.notes && (
        <div style={{ marginTop: 16 }}>
          <Field label="Notes" value={data.notes} />
        </div>
      )}
      <div className="form-hint" style={{ marginTop: 16 }}>
        To edit this Work Order, use the Edit action from the Work Orders list.
      </div>
    </div>
  );
}

function ConsultingInvoicesTab({ invoices }) {
  const rows = invoices || [];
  const outstanding = rows.filter(i => !['void', 'draft'].includes(i.status)).reduce((s, i) => s + Math.max(0, i.amount - (i.verified_paid || 0)), 0);
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>Invoices ({rows.length}) <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--muted)' }}>· outstanding {fmt(outstanding)}</span></h3>
      {rows.length === 0 ? (
        <StateBlock type="empty" title="No invoices yet" message="Use New consulting invoice. A client PO is not required." />
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>Invoice #</th><th>Date</th><th>Period</th><th>PO</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Paid</th><th>Due</th><th>Status</th></tr>
          </thead>
          <tbody>
            {rows.map(i => (
              <tr key={i.id}>
                <td><EntityLink type="ar_invoice" id={i.id} style={{ fontWeight: 600 }}>{i.invoice_number}</EntityLink></td>
                <td>{i.invoice_date}</td>
                <td>{i.period_start ? `${i.period_start} – ${i.period_end || '…'}` : '—'}</td>
                <td>{i.po_number ? <EntityLink type="purchase_order" id={i.po_id}>{i.po_number}</EntityLink> : '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmt(i.amount)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(i.verified_paid)}</td>
                <td>{i.due_date || '—'}</td>
                <td><Badge variant={i.status === 'paid' ? 'success' : i.status === 'partial' ? 'warning' : undefined}>{i.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="form-hint" style={{ marginTop: 12 }}>Open an invoice to submit it, record payments, edit its lines or download the PDF.</div>
    </div>
  );
}

function PurchaseOrdersTab({ purchaseOrders, isConsulting }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>Purchase Orders ({(purchaseOrders || []).length})</h3>
      {(!purchaseOrders || purchaseOrders.length === 0) ? (
        <StateBlock type="empty" title="No purchase orders yet" />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>PO Number</th>{!isConsulting && <><th style={{ textAlign: 'right' }}>Sell Rate</th>
              <th style={{ textAlign: 'right' }}>Buy Rate</th></>}<th>Period</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {purchaseOrders.map(po => (
              <tr key={po.id}>
                <td><EntityLink type="purchase_order" id={po.id} style={{ fontWeight: po.status === 'open' ? 600 : 400 }}>{po.po_number}</EntityLink></td>
                {!isConsulting && <><td style={{ textAlign: 'right' }}>{po.sell_rate ? `$${po.sell_rate}/hr` : '—'}</td>
                <td style={{ textAlign: 'right' }}>{po.buy_rate ? `$${po.buy_rate}/hr` : '—'}</td></>}
                <td>{po.period_start} – {po.period_end}</td>
                <td><Badge variant={po.status === 'open' ? 'success' : 'neutral'}>{po.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
